import type { BuilderContent } from "@builder.io/sdk-react";

/**
 * Project one array of blocks from `entry.data` into a `BuilderContent` whose
 * default `data.blocks` IS that array, so it can be handed to `<Content>`
 * unchanged. Returns `null` when the source is absent or empty.
 *
 * This is how one entry drives more than one page region: a model carries
 * named `uiBlocks` fields beside its main canvas, and each region renders its
 * own projection (`pdp-glp-1-pricing` regions, ET-216; the `pdp-inline-content`
 * below-accordions region, COM-793).
 *
 * Why this rather than the SDK's `<Blocks>`: `Blocks` used outside a `<Content>`
 * tree needs a hand-constructed `BuilderContextInterface` (see its
 * `BlocksProps`), which means reimplementing what `<Content>` already sets up —
 * apiKey, registered components, enrichment. Swapping `data.blocks` instead
 * keeps every region on the single proven render path
 * (`PrefetchedBuilderContent`).
 */
export function projectEntryRegion(
  entry: BuilderContent | null | undefined,
  key: string,
  transform?: (blocks: unknown[]) => unknown[],
): BuilderContent | null {
  if (!entry) return null;

  const data = (entry.data ?? {}) as Record<string, unknown>;
  const source = data[key];
  if (!Array.isArray(source) || !source.length) return null;

  const blocks = transform ? transform(source) : source;
  if (!blocks.length) return null;

  return { ...entry, data: { ...data, blocks } } as BuilderContent;
}

/** The blocks a projection ended up with, for presence checks. */
export const regionBlocksOf = (content: BuilderContent | null): unknown[] =>
  ((content?.data as { blocks?: unknown[] } | undefined)?.blocks ??
    []) as unknown[];

/**
 * Give a projected SECONDARY region its own content id (`<entryId>-<region>`),
 * so the SDK stops treating it as the entry itself.
 *
 * Why: in Builder's open-in-new-tab Preview (`isPreviewing() && !isEditing()`,
 * `builder.preview=<model>` + `builder.overrides.<model>=<entryId>` in the URL),
 * every `<Content>` of the previewed model whose `content.id` matches the
 * override re-fetches the FULL entry on mount and merges it in (sdk-react
 * v4.2.2, `EnableEditor` mount effect). For a projection that overwrites the
 * swapped `data.blocks` with the main canvas, so the region shows the canvas a
 * second time. A distinct id fails that match, so the projection stands.
 *
 * Inside the SDK the id also names the wrapper (`builder-content-id`, its
 * class, and the scoped custom-CSS selector — all derived from the same value,
 * so they stay consistent) and attributes impressions. Pair with
 * `canTrack={false}` on the secondary `<Content>`: the derived id is not a
 * real entry, and one entry shouldn't log two impressions per page view.
 * `canTrack={false}` is REQUIRED, not cosmetic: the SDK renders Builder A/B
 * variants only when tracking is on, and each variant's `data.blocks` is that
 * variant's MAIN canvas — the same duplication by another route. It also
 * disables click tracking for the region, and an A/B-tested entry's two
 * regions may then show different variations (the primary picks by cookie,
 * the secondary shows the loader-resolved one).
 *
 * Known trade-offs (accepted for COM-793, unverified without editor access):
 * visual-editor click-to-select maps DOM to entries by `builder-content-id`,
 * so selecting blocks inside a secondary region may not resolve to the entry;
 * and the editor applies entry-level custom breakpoints only to a matching id,
 * so the region ignores them until the next content update.
 * Never apply this to the primary region — it must keep the real id.
 */
export function scopeRegionId(
  content: BuilderContent | null,
  region: string,
): BuilderContent | null {
  if (!content) return null;
  return { ...content, id: `${content.id ?? "entry"}-${region}` };
}
